import { applications, portalConfig } from "../config/applications";

export type ReviewStatus = "pending" | "approved" | "rejected";
export type ApplicationCode = "drf" | "design" | "ocr" | "bridge" | "ai";

export interface AccountProfile {
  id: string;
  name: string;
  username: string;
  email: string;
  emailVerified: boolean;
  administrator: boolean;
  active: boolean;
  verified: boolean;
  reviewStatus: ReviewStatus;
  requestId: string | null;
  requestedApplications: ApplicationCode[];
  grantedApplications: ApplicationCode[];
  effectiveApplications: ApplicationCode[];
  reason: string | null;
  decisionNote: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
}

export const authBaseUrl = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)
  ? "http://localhost:8788"
  : portalConfig.authUrl;

export const applicationNames: Record<ApplicationCode, string> = {
  drf: "Drawing Requests",
  design: "Design Project Tracking",
  ocr: "OCR Extraction System",
  bridge: "Production Bridge Calculator",
  ai: "AI Knowledge Assistant",
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body) headers.set("content-type", "application/json");
  const response = await fetch(`${authBaseUrl}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
  const data = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) {
    const error = new Error(String(data.error ?? data.code ?? data.message ?? "REQUEST_FAILED"));
    Object.assign(error, { status: response.status, retryAfter: response.headers.get("X-Retry-After"), details: data.details });
    throw error;
  }
  return data as T;
}

export function safeReturnTo(value: string | null): string | null {
  if (!value) return null;
  try {
    const candidate = new URL(value);
    return applications.some((application) => application.enabled && application.publicUrl && new URL(application.publicUrl).href === candidate.href)
      ? candidate.href
      : null;
  } catch {
    return null;
  }
}

export function accessCodeForUrl(value: string): ApplicationCode | null {
  return applications.find((application) => application.publicUrl && new URL(application.publicUrl).href === value)?.accessCode ?? null;
}

export function postLoginDestination(profile: Pick<AccountProfile, "effectiveApplications">, returnTo: string | null): string {
  const destination = safeReturnTo(returnTo);
  if (!destination) return "/";
  const application = applications.find((item) => item.publicUrl && new URL(item.publicUrl).href === destination);
  if (application?.publicAccess) return destination;
  if (application?.accessCode && profile.effectiveApplications.includes(application.accessCode)) return destination;
  return application ? `/profile?application=${encodeURIComponent(application.id)}` : "/";
}

export const authApi = {
  config: () => request<{ signupEnabled: boolean }>("/api/public/config"),
  signup: (body: Record<string, unknown>) => request<{ status: string; verificationEmailSent: boolean }>("/api/public/signup", { method: "POST", body: JSON.stringify(body) }),
  signIn: (identifier: string, password: string) => request<Record<string, unknown>>(`/api/auth/sign-in/${identifier.includes("@") ? "email" : "username"}`, {
    method: "POST",
    body: JSON.stringify({ [identifier.includes("@") ? "email" : "username"]: identifier, password, callbackURL: portalConfig.productionUrl }),
  }),
  signOut: () => request<{ success: boolean }>("/api/auth/sign-out", { method: "POST", body: "{}" }),
  profile: () => request<{ profile: AccountProfile }>("/api/account/profile"),
  updateProfile: (name: string) => request<{ profile: AccountProfile }>("/api/account/profile", { method: "PATCH", body: JSON.stringify({ name }) }),
  resendVerification: () => request<{ status: boolean }>("/api/account/resend-verification", { method: "POST", body: "{}" }),
  changePassword: (currentPassword: string, newPassword: string) => request<{ status: boolean }>("/api/account/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }),
  requestReset: (email: string) => request<{ status: boolean }>("/api/auth/request-password-reset", { method: "POST", body: JSON.stringify({ email, redirectTo: `${portalConfig.productionUrl}/reset-password` }) }),
  resetPassword: (token: string, newPassword: string) => request<{ status: boolean }>("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ token, newPassword }) }),
};
